import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';
import useAuth from '../hooks/useAuth';
import { API_BASE_URL } from '../config';
import { IMaskInput } from 'react-imask';
import Papa from 'papaparse';
import { useDropzone } from 'react-dropzone';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import {
    Box, Button, Container, Typography, Stack, Chip, createTheme, ThemeProvider,
    Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Avatar, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, MenuItem, Select, FormControl, InputLabel, alpha, Snackbar, Alert,
    CircularProgress, LinearProgress, DialogContentText, useMediaQuery, useTheme,
    Divider, InputAdornment, Checkbox, Toolbar, ButtonGroup, FormControlLabel,
    CssBaseline, styled
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Add, DeleteOutline, ModeEditOutline, PowerSettingsNew, CheckCircle, UploadFile,
    Visibility, VisibilityOff, Download, GroupOff, DeleteForever, Checklist, Search,
    FilterList, Close, History, Security, Save, Apps
} from '@mui/icons-material';

const accessLevels = [
    { value: 'superadmin', label: 'Super Admin', color: 'secondary' },
    { value: 'admin', label: 'Admin', color: 'error' },
    { value: 'editor', label: 'Editor', color: 'info' },
    { value: 'viewer', label: 'Visualizador', color: 'default' },
];

const accessLevelConfig = accessLevels.reduce((acc, level) => { acc[level.value] = level; return acc; }, {});

// --- TEMA IGUAL AO DA DASHBOARD ---
const customTheme = createTheme({
    palette: {
        mode: 'dark',
        primary: { main: '#315e9a', contrastText: '#ffffff' },
        secondary: { main: '#90caf9' },
        background: { default: '#0f1218', paper: '#1a1d24' },
        divider: 'rgba(0,0,0,0)',
    },
    typography: { button: { textTransform: 'none', fontWeight: 600 } },
    shape: { borderRadius: 12 },
});

// --- PAPER ESCURO PADRONIZADO ---
const DarkPaper = styled(Paper)(({ theme, selected }) => ({
    backgroundColor: selected ? alpha(theme.palette.primary.main, 0.15) : theme.palette.background.paper,
    backgroundImage: 'none',
    border: 'none',
    boxShadow: selected ? `0 0 0 1px ${alpha(theme.palette.primary.main, 0.5)}` : '0 8px 24px rgba(0,0,0,0.35)',
    transition: 'all 0.2s ease-in-out',
    borderRadius: 16,
}));

const CpfMaskAdapter = React.forwardRef(function CpfMaskAdapter(props, ref) {
    const { onChange, ...other } = props;
    return (
        <IMaskInput
            {...other}
            mask="000.000.000-00"
            definitions={{ '#': /[1-9]/, }}
            inputRef={ref}
            onAccept={(value) => onChange({ target: { name: props.name, value } })}
            overwrite
        />
    );
});

// --- HISTÓRICO DE LOGIN (AJUSTADO) ---
const LoginHistoryModal = ({ open, onClose, logs, title, loading }) => (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Stack>
                <Typography variant="h6" fontWeight="bold">Histórico de Acesso</Typography>
                <Typography variant="body2" color="text.secondary">{title}</Typography>
            </Stack>
            <IconButton onClick={onClose}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: 'transparent' }}>
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
            ) : logs.length === 0 ? (
                <Box sx={{ textAlign: 'center', p: 4, color: 'text.secondary' }}>
                    <History sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
                    <Typography>Nenhum registro de acesso encontrado.</Typography>
                </Box>
            ) : (
                <TableContainer>
                    <Table size="small">
                        <TableHead sx={{ bgcolor: alpha('#90caf9', 0.05) }}>
                            <TableRow>
                                <TableCell><strong>Data e Hora</strong></TableCell>
                                <TableCell><strong>Usuário</strong></TableCell>
                                <TableCell><strong>IP</strong></TableCell>
                                <TableCell><strong>Dispositivo/Navegador</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {logs.map((log) => (
                                <TableRow key={log.id} hover>
                                    <TableCell>{format(parseISO(log.createdAt), "dd/MM/yyyy 'às' HH:mm:ss", { locale: ptBR })}</TableCell>
                                    <TableCell>{log.user ? log.user.name : '---'}</TableCell>
                                    <TableCell>{log.ipAddress || 'N/A'}</TableCell>
                                    <TableCell sx={{ maxWidth: 200 }}>
                                        <Tooltip title={log.userAgent || ''}>
                                            <Typography variant="caption" noWrap display="block">{log.userAgent || 'Desconhecido'}</Typography>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </DialogContent>
        <DialogActions><Button onClick={onClose}>Fechar</Button></DialogActions>
    </Dialog>
);

// --- BULK IMPORT (AJUSTADO) ---
const BulkImportModal = ({ open, onClose, onImport, units }) => {
    const theme = useTheme();
    const [file, setFile] = useState(null);
    const [selectedUnitId, setSelectedUnitId] = useState('');
    const [fileName, setFileName] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState('');
    const [tempPassword, setTempPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    const processFile = (selectedFile) => {
        if (selectedFile) {
            if (selectedFile.type !== 'text/csv' && !selectedFile.name.endsWith('.csv')) {
                setError('Formato de arquivo inválido. Por favor, envie um arquivo .csv');
                setFile(null); setFileName('');
            } else {
                setFile(selectedFile); setFileName(selectedFile.name); setError('');
            }
        }
    };
    const onDrop = useCallback((acceptedFiles) => processFile(acceptedFiles[0]), []);
    const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, accept: { 'text/csv': ['.csv'] }, multiple: false });

    const handleImportClick = async () => {
        if (!file) { setError('Por favor, selecione um arquivo.'); return; }
        if (!selectedUnitId) { setError('Por favor, selecione uma unidade.'); return; }
        if (!tempPassword) { setError('A senha provisória é obrigatória.'); return; }
        setIsProcessing(true); setError('');
        Papa.parse(file, {
            header: true, skipEmptyLines: true,
            complete: (results) => { onImport(results.data, selectedUnitId, tempPassword); setIsProcessing(false); onClose(); },
            error: () => { setError('Erro ao processar o arquivo CSV.'); setIsProcessing(false); }
        });
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle fontWeight="bold">Importar Colaboradores em Massa</DialogTitle>
            <DialogContent dividers sx={{ borderColor: 'transparent' }}>
                <Stack spacing={3} sx={{ p: 1 }}>
                    <Box>
                        <Typography variant="h6" gutterBottom>Instruções</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Use um arquivo <strong>.csv</strong> com cabeçalho na primeira linha.
                        </Typography>
                        <Paper variant="outlined" sx={{ p: 2, mt: 1, backgroundColor: alpha('#90caf9', 0.05), borderColor: 'rgba(255,255,255,0.05)' }}>
                            <Typography variant="body2"><strong>Mínimo:</strong> <code>Nome,Email</code></Typography>
                            <Typography variant="body2" sx={{ mt: 1 }}><strong>Completo:</strong> <code>Nome,Email,CPF,Cargo</code></Typography>
                        </Paper>
                    </Box>
                    <FormControl fullWidth required>
                        <InputLabel id="unit-import-label">Unidade de Destino</InputLabel>
                        <Select labelId="unit-import-label" value={selectedUnitId} onChange={(e) => setSelectedUnitId(e.target.value)} label="Unidade de Destino">
                            {units.map(unit => (<MenuItem key={unit.id} value={unit.id}>{unit.name} {unit.isMatriz ? '(Matriz)' : ''}</MenuItem>))}
                        </Select>
                    </FormControl>
                    <TextField
                        fullWidth required type={showPassword ? 'text' : 'password'}
                        label="Senha Provisória para Todos" value={tempPassword}
                        onChange={(e) => setTempPassword(e.target.value)}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowPassword(!showPassword)} onMouseDown={(e) => e.preventDefault()} edge="end">
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Box {...getRootProps()} sx={{
                        border: '2px dashed',
                        borderColor: isDragActive ? 'primary.main' : 'rgba(255,255,255,0.1)',
                        borderRadius: 2, p: 4, textAlign: 'center', cursor: 'pointer',
                        backgroundColor: isDragActive ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                        transition: 'border-color 0.3s, background-color 0.3s'
                    }}>
                        <input {...getInputProps()} />
                        <UploadFile sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                        {isDragActive ? <Typography>Solte o arquivo aqui...</Typography> : <Typography>Arraste e solte o arquivo .csv aqui, ou clique para selecionar.</Typography>}
                    </Box>
                    {fileName && <Typography variant="body2" color="text.secondary" align="center">Arquivo selecionado: {fileName}</Typography>}
                    {error && <Alert severity="error">{error}</Alert>}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ p: '16px 24px' }}>
                <Button onClick={onClose}>Cancelar</Button>
                <Button onClick={handleImportClick} variant="contained" disabled={!file || !selectedUnitId || !tempPassword || isProcessing}>
                    {isProcessing ? <CircularProgress size={24} /> : 'Importar Usuários'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// --- USER FORM MODAL (AJUSTADO) ---
const UserFormModal = ({ open, onClose, onSubmit, initialData, units }) => {
    const isEditing = Boolean(initialData);
    const [selectedRole, setSelectedRole] = useState(initialData?.role || 'viewer');
    const [selectedUnitId, setSelectedUnitId] = useState(initialData?.unitId || '');

    useEffect(() => {
        if (open) {
            setSelectedRole(initialData?.role || 'viewer');
            setSelectedUnitId(initialData?.unitId || '');
        }
    }, [open, initialData]);

    const handleSubmitInternal = (event) => {
        event.preventDefault();
        const formData = new FormData(event.currentTarget);
        const userData = {
            name: formData.get('name'), email: formData.get('email'),
            cpf: formData.get('cpf'), cargo: formData.get('cargo'),
            role: formData.get('role'), unitId: formData.get('unitId') || null,
        };
        const password = formData.get('password');
        if (password && !isEditing) userData.password = password;
        onSubmit(userData);
    };

    return (
        <Dialog open={open} onClose={onClose} PaperProps={{ component: 'form', onSubmit: handleSubmitInternal, sx: { borderRadius: 3 } }} fullWidth maxWidth="sm">
            <DialogTitle fontWeight="bold">{isEditing ? 'Editar Colaborador' : 'Adicionar Novo Membro'}</DialogTitle>
            <DialogContent dividers sx={{ borderColor: 'transparent' }}>
                <Stack spacing={2.5} sx={{ pt: 1, mt: 1 }}>
                    <TextField name="name" label="Nome Completo" fullWidth required defaultValue={initialData?.name || ''} />
                    <TextField name="email" label="Endereço de E-mail" type="email" fullWidth required defaultValue={initialData?.email || ''} />
                    <TextField name="cpf" label="CPF" fullWidth defaultValue={initialData?.cpf || ''} InputProps={{ inputComponent: CpfMaskAdapter }} inputProps={{ name: 'cpf' }} />
                    {!isEditing && (<TextField name="password" label="Senha Provisória" type="password" fullWidth required />)}
                    <TextField name="cargo" label="Cargo" fullWidth required defaultValue={initialData?.cargo || ''} helperText="Ex: Analista de Marketing, Desenvolvedor Pleno" />
                    <FormControl fullWidth>
                        <InputLabel id="role-select-label">Nível de Acesso no Sistema</InputLabel>
                        <Select labelId="role-select-label" name="role" value={selectedRole} onChange={(e) => setSelectedRole(e.target.value)} label="Nível de Acesso no Sistema">
                            {accessLevels.map(level => (<MenuItem key={level.value} value={level.value}>{level.label}</MenuItem>))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth>
                        <InputLabel id="unit-select-label">Unidade (Matriz/Filial)</InputLabel>
                        <Select labelId="unit-select-label" name="unitId" value={selectedUnitId} onChange={(e) => setSelectedUnitId(e.target.value)} label="Unidade (Matriz/Filial)">
                            <MenuItem value=""><em>Nenhuma</em></MenuItem>
                            {units.map(unit => (
                                <MenuItem key={unit.id} value={unit.id}>{unit.name} {unit.isMatriz ? '(Matriz)' : ''}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>
            </DialogContent>
            <DialogActions sx={{ p: '16px 24px' }}>
                <Button onClick={onClose}>Cancelar</Button>
                <Button type="submit" variant="contained">{isEditing ? 'Salvar Alterações' : 'Adicionar Membro'}</Button>
            </DialogActions>
        </Dialog>
    );
};

// --- CARD MOBILE ESCURO ---
const CollaboratorCardMobile = ({ collaborator, isSelected, isSelf, selectionModeActive, onSelect, onEdit, onDelete, onHistory, onPermissions, onToggleStatus, statusUpdatingId, accessLevelConfig }) => {
    const isActive = collaborator.isActive !== false;
    const avatarCompletoUrl = collaborator.avatarUrl ? new URL(`/uploads/${collaborator.avatarUrl}`, API_BASE_URL).href : '';

    return (
        <motion.div layout>
            <DarkPaper selected={isSelected} sx={{ p: 2, position: 'relative' }}>
                {selectionModeActive && !isSelf && (
                    <Checkbox color="primary" checked={isSelected} onChange={onSelect} sx={{ position: 'absolute', top: 8, left: 8, zIndex: 1 }} />
                )}
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1} sx={{ pl: selectionModeActive && !isSelf ? 4 : 0 }}>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ flexGrow: 1, overflow: 'hidden' }}>
                        <Avatar sx={{ bgcolor: 'primary.main', color: 'primary.contrastText' }} src={avatarCompletoUrl}>
                            {!collaborator.avatarUrl && collaborator.name ? collaborator.name.split(' ').map(n => n[0]).join('').toUpperCase() : ''}
                        </Avatar>
                        <Box sx={{ overflow: 'hidden' }}>
                            <Typography variant="subtitle1" fontWeight="bold">{collaborator.name}</Typography>
                            <Typography variant="body2" color="text.secondary">{collaborator.cargo || 'Não definido'}</Typography>
                        </Box>
                    </Stack>
                    <Stack direction="row">
                        <Tooltip title="Editar"><IconButton size="small" onClick={onEdit}><ModeEditOutline fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Histórico"><IconButton size="small" onClick={onHistory}><History fontSize="small" /></IconButton></Tooltip>
                        {!isSelf && (<Tooltip title="Permissões"><IconButton size="small" onClick={onPermissions}><Security fontSize="small" /></IconButton></Tooltip>)}
                        {!isSelf && (<Tooltip title="Remover"><IconButton size="small" onClick={onDelete}><DeleteOutline fontSize="small" /></IconButton></Tooltip>)}
                    </Stack>
                </Stack>
                <Divider sx={{ my: 1.5, borderColor: 'rgba(255,255,255,0.05)' }} />
                <Stack spacing={1.5}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="text.secondary">CPF</Typography>
                        <Typography variant="body2" fontWeight="500">{collaborator.cpf || '---'}</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="text.secondary">Unidade</Typography>
                        {collaborator.unit ? (
                            <Chip label={`${collaborator.unit.name} ${collaborator.unit.isMatriz ? '(Matriz)' : ''}`} size="small" variant="outlined" color={collaborator.unit.isMatriz ? 'primary' : 'default'} />
                        ) : (<Chip label="N/A" size="small" variant="outlined" />)}
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="text.secondary">Último Acesso</Typography>
                        <Typography variant="body2" fontWeight="500">{collaborator.lastLogin ? format(parseISO(collaborator.lastLogin), 'dd/MM/yy HH:mm', { locale: ptBR }) : 'Nunca acessou'}</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="text.secondary">Acesso</Typography>
                        <Chip label={accessLevelConfig[collaborator.role]?.label || collaborator.role} color={accessLevelConfig[collaborator.role]?.color || 'default'} size="small" />
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="text.secondary">Status</Typography>
                        <Tooltip title={isActive ? 'Desativar' : 'Reativar'}>
                            <span>
                                <Chip
                                    icon={statusUpdatingId === collaborator.id ? <CircularProgress size={14} color="inherit" /> : <PowerSettingsNew fontSize="small" />}
                                    label={isActive ? 'Ativo' : 'Desligado'} color={isActive ? 'success' : 'error'} size="small"
                                    onClick={!isSelf ? onToggleStatus : null}
                                    disabled={statusUpdatingId === collaborator.id || isSelf}
                                    sx={{ cursor: !isSelf ? 'pointer' : 'default' }}
                                />
                            </span>
                        </Tooltip>
                    </Stack>
                </Stack>
            </DarkPaper>
        </motion.div>
    );
};

const CollaboratorsPage = () => {
    const [collaborators, setCollaborators] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState({ type: null, data: null });
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [licenseUsage, setLicenseUsage] = useState({ used: 0, limit: 0 });
    const [error, setError] = useState('');
    const [units, setUnits] = useState([]);
    const { user, loading: authLoading, isAuthenticated } = useAuth();
    const [confirmDialog, setConfirmDialog] = useState({ open: false, user: null, type: null });
    const [statusUpdatingId, setStatusUpdatingId] = useState(null);
    const isMobile = useMediaQuery(theme => theme.breakpoints.down('md'));
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [selected, setSelected] = useState([]);
    const [selectionModeActive, setSelectionModeActive] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterRole, setFilterRole] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all');
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
    const [tempFilterRole, setTempFilterRole] = useState('all');
    const [tempFilterStatus, setTempFilterStatus] = useState('all');
    const [historyModalOpen, setHistoryModalOpen] = useState(false);
    const [historyLogs, setHistoryLogs] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyTitle, setHistoryTitle] = useState('');
    const [openPermissionsModal, setOpenPermissionsModal] = useState(false);
    const [userForPermissions, setUserForPermissions] = useState(null);
    const [availableSoftwares, setAvailableSoftwares] = useState([]);
    const [userSoftwares, setUserSoftwares] = useState([]);
    const [savingPermissions, setSavingPermissions] = useState(false);
    const [openSelectionSoftwareModal, setOpenSelectionSoftwareModal] = useState(false);
    const [selectionSoftwares, setSelectionSoftwares] = useState([]);
    const [savingSelectionSoftwares, setSavingSelectionSoftwares] = useState(false);

    const fetchData = useCallback(async () => {
        setLoading(true); setError('');
        try {
            const [usersResponse, usageResponse, organizationResponse] = await Promise.all([
                api.get('/users'), api.get('/users/usage'), api.get('/organization')
            ]);
            setCollaborators(usersResponse.data);
            setLicenseUsage(usageResponse.data);
            if (organizationResponse.data && organizationResponse.data.units) {
                setUnits(organizationResponse.data.units.sort((a, b) => b.isMatriz - a.isMatriz));
            } else setUnits([]);
        } catch (err) {
            const errorMessage = err.response?.data?.message || 'Não foi possível carregar os dados.';
            setSnackbar({ open: true, message: errorMessage, severity: 'error' });
            setError(errorMessage);
        } finally { setLoading(false); }
    }, []);

    useEffect(() => {
        if (!authLoading && isAuthenticated && user?.companyId) fetchData();
        else if (!authLoading) {
            setLoading(false);
            setError('Não foi possível carregar os dados. Verifique sua autenticação e associação com uma empresa.');
        }
    }, [authLoading, isAuthenticated, user, fetchData]);

    const filteredCollaborators = useMemo(() => collaborators.filter(collaborator => {
        const searchMatch = searchTerm === '' ||
            collaborator.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            collaborator.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (collaborator.cpf && collaborator.cpf.includes(searchTerm));
        const roleMatch = filterRole === 'all' || collaborator.role === filterRole;
        const statusMatch = filterStatus === 'all' || (filterStatus === 'active' && collaborator.isActive !== false) || (filterStatus === 'inactive' && collaborator.isActive === false);
        return searchMatch && roleMatch && statusMatch;
    }), [collaborators, searchTerm, filterRole, filterStatus]);

    const handleOpenGlobalHistory = async () => {
        setHistoryTitle('Visualizando acessos de todos os usuários');
        setHistoryModalOpen(true); setHistoryLoading(true);
        try { const response = await api.get('/users/login-history'); setHistoryLogs(response.data); }
        catch { setSnackbar({ open: true, message: 'Erro ao carregar histórico global.', severity: 'error' }); setHistoryLogs([]); }
        finally { setHistoryLoading(false); }
    };
    const handleOpenUserHistory = async (targetUser) => {
        setHistoryTitle(`Visualizando acessos de: ${targetUser.name}`);
        setHistoryModalOpen(true); setHistoryLoading(true);
        try { const response = await api.get(`/users/${targetUser.id}/login-history`); setHistoryLogs(response.data); }
        catch { setSnackbar({ open: true, message: 'Erro ao carregar histórico do usuário.', severity: 'error' }); setHistoryLogs([]); }
        finally { setHistoryLoading(false); }
    };

    const handleOpenPermissions = async (targetUser) => {
        setUserForPermissions(targetUser); setAvailableSoftwares([]); setUserSoftwares([]); setOpenPermissionsModal(true);
        try {
            const companyResponse = await api.get('/softwares/my-company');
            setAvailableSoftwares(companyResponse.data);
            const userResponse = await api.get(`/softwares/user/${targetUser.id}`);
            setUserSoftwares(userResponse.data.map(s => s.id));
        } catch { setSnackbar({ open: true, message: 'Erro ao carregar permissões.', severity: 'error' }); }
    };
    const handleTogglePermission = (softwareId) => setUserSoftwares(prev => prev.includes(softwareId) ? prev.filter(id => id !== softwareId) : [...prev, softwareId]);
    const handleSavePermissions = async () => {
        setSavingPermissions(true);
        try {
            await api.post(`/softwares/user/${userForPermissions.id}/assign`, { softwareIds: userSoftwares });
            setSnackbar({ open: true, message: 'Permissões atualizadas!', severity: 'success' });
            setOpenPermissionsModal(false);
        } catch { setSnackbar({ open: true, message: 'Erro ao salvar permissões.', severity: 'error' }); }
        finally { setSavingPermissions(false); }
    };

    const handleOpenSelectionSoftware = async () => {
        setAvailableSoftwares([]); setSelectionSoftwares([]); setOpenSelectionSoftwareModal(true);
        try { const companyResponse = await api.get('/softwares/my-company'); setAvailableSoftwares(companyResponse.data); }
        catch { setSnackbar({ open: true, message: 'Erro ao carregar softwares.', severity: 'error' }); }
    };
    const handleToggleSelectionSoftware = (softwareId) => setSelectionSoftwares(prev => prev.includes(softwareId) ? prev.filter(id => id !== softwareId) : [...prev, softwareId]);
    const handleSaveSelectionSoftware = async () => {
        if (selectionSoftwares.length === 0) { setSnackbar({ open: true, message: 'Selecione ao menos um software.', severity: 'warning' }); return; }
        setSavingSelectionSoftwares(true);
        try {
            await api.post('/softwares/users/bulk-assign', { userIds: selected, softwareIds: selectionSoftwares });
            setSnackbar({ open: true, message: 'Softwares vinculados!', severity: 'success' });
            setOpenSelectionSoftwareModal(false); setSelected([]); setSelectionModeActive(false);
        } catch { setSnackbar({ open: true, message: 'Erro ao vincular.', severity: 'error' }); }
        finally { setSavingSelectionSoftwares(false); }
    };

    const handleCloseModal = () => setModal({ type: null, data: null });
    const handleSaveUser = async (userData) => {
        try {
            if (modal.type === 'edit') {
                const response = await api.put(`/users/${modal.data.id}`, userData);
                setCollaborators(curr => curr.map(u => u.id === response.data.id ? response.data : u));
                setSnackbar({ open: true, message: 'Colaborador atualizado!', severity: 'success' });
            } else {
                await api.post('/users', userData);
                setSnackbar({ open: true, message: 'Colaborador adicionado!', severity: 'success' });
                fetchData();
            }
        } catch (err) {
            setSnackbar({ open: true, message: err.response?.data?.message || 'Erro ao salvar.', severity: 'error' });
        }
        handleCloseModal();
    };
    const handleImport = async (users, unitId, tempPassword) => {
        if (!users || users.length === 0) { setSnackbar({ open: true, message: 'Nenhum dado válido.', severity: 'warning' }); return; }
        try {
            const response = await api.post('/users/bulk-import', { users, unitId, tempPassword });
            const { createdCount, errors } = response.data;
            let message = `${createdCount} usuários importados com sucesso.`;
            if (errors.length > 0) message += ` ${errors.length} não importados.`;
            setSnackbar({ open: true, message, severity: errors.length > 0 ? 'warning' : 'success' });
            fetchData();
        } catch (err) { setSnackbar({ open: true, message: err.response?.data?.message || 'Erro na importação.', severity: 'error' }); }
    };
    const handleExportCsv = async () => {
        setIsExporting(true);
        try {
            const response = await api.get('/users/export', { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url; link.setAttribute('download', 'colaboradores.csv');
            document.body.appendChild(link); link.click(); link.parentNode.removeChild(link);
        } catch { setSnackbar({ open: true, message: 'Erro ao exportar.', severity: 'error' }); }
        finally { setIsExporting(false); }
    };
    const handleDeleteUser = async () => {
        if (modal.type === 'delete' && modal.data) {
            try {
                await api.delete(`/users/${modal.data.id}`);
                setSnackbar({ open: true, message: 'Colaborador removido!', severity: 'warning' });
                fetchData();
            } catch { setSnackbar({ open: true, message: 'Erro ao remover.', severity: 'error' }); }
            handleCloseModal();
        }
    };
    const openConfirmToggle = (userToToggle) => setConfirmDialog({ open: true, user: userToToggle, type: 'toggleStatus' });
    const closeConfirmToggle = () => setConfirmDialog({ open: false, user: null, type: null });
    const handleToggleStatus = async () => {
        const userToToggle = confirmDialog.user; if (!userToToggle) return;
        const id = userToToggle.id;
        const newIsActive = !(userToToggle.isActive !== false);
        setStatusUpdatingId(id); closeConfirmToggle();
        try {
            await api.patch(`/users/${id}`, { isActive: newIsActive });
            setCollaborators(prev => prev.map(u => u.id === id ? { ...u, isActive: newIsActive } : u));
            setSnackbar({ open: true, message: `Status: "${newIsActive ? 'Ativo' : 'Desligado'}"`, severity: 'success' });
        } catch (err) {
            setSnackbar({ open: true, message: `Erro: ${err.response?.data?.message || 'Falha ao alterar'}`, severity: 'error' });
        } finally { setStatusUpdatingId(null); }
    };
    const handleCloseSnackbar = (event, reason) => { if (reason === 'clickaway') return; setSnackbar({ ...snackbar, open: false }); };
    const handleToggleSelectionMode = () => { if (selectionModeActive) setSelected([]); setSelectionModeActive(!selectionModeActive); };
    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            setSelected(filteredCollaborators.filter(c => c.id !== user.id).map(n => n.id));
            return;
        }
        setSelected([]);
    };
    const handleClick = (event, id) => {
        if (id === user.id) return;
        const selectedIndex = selected.indexOf(id);
        let newSelected = [];
        if (selectedIndex === -1) newSelected = newSelected.concat(selected, id);
        else if (selectedIndex === 0) newSelected = newSelected.concat(selected.slice(1));
        else if (selectedIndex === selected.length - 1) newSelected = newSelected.concat(selected.slice(0, -1));
        else if (selectedIndex > 0) newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
        setSelected(newSelected);
    };
    const isSelected = (id) => selected.indexOf(id) !== -1;
    const numSelected = selected.length;
    const rowCount = filteredCollaborators.filter(c => c.id !== user.id).length;
    const handleBulkAction = async (action) => {
        const actionVerb = action === 'delete' ? 'excluir' : 'desativar';
        if (!window.confirm(`Você tem certeza que deseja ${actionVerb} os ${numSelected} usuários selecionados?`)) return;
        try {
            await api.post('/users/bulk-action', { userIds: selected, action });
            setSnackbar({ open: true, message: 'Ação em massa concluída!', severity: 'success' });
            setSelected([]); fetchData();
        } catch (error) {
            setSnackbar({ open: true, message: error.response?.data?.message || `Falha ao ${actionVerb}.`, severity: 'error' });
        }
    };
    const progress = licenseUsage.limit > 0 ? (licenseUsage.used / licenseUsage.limit) * 100 : 0;
    const handleOpenFilterModal = () => { setTempFilterRole(filterRole); setTempFilterStatus(filterStatus); setIsFilterModalOpen(true); };
    const handleCloseFilterModal = () => setIsFilterModalOpen(false);
    const handleApplyFilters = () => { setFilterRole(tempFilterRole); setFilterStatus(tempFilterStatus); handleCloseFilterModal(); };
    const handleClearFilters = () => { setTempFilterRole('all'); setTempFilterStatus('all'); };

    return (
        <ThemeProvider theme={customTheme}>
            <CssBaseline />
            <Box sx={{ width: '100%', minHeight: '100vh', bgcolor: 'background.default', pb: 8 }}>
                <Container maxWidth="lg" sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems="center" spacing={2} sx={{ mb: 4 }}>
                        <Box>
                            <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight={700} color="text.primary">Equipe e Colaboradores</Typography>
                            <Typography color="text.secondary">Adicione e gerencie os membros da sua organização.</Typography>
                        </Box>

                        <Stack direction="row" spacing={1} alignItems="center">
                            {selectionModeActive ? (
                                <Button variant="contained" color="secondary" onClick={handleToggleSelectionMode}>Cancelar</Button>
                            ) : (
                                <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={{ xs: 1, sm: 2 }} alignItems="center">
                                    <ButtonGroup variant="outlined" aria-label="Grupo de botões de gerenciamento" sx={{ borderColor: 'rgba(255,255,255,0.1)' }}>
                                        <Tooltip title="Log de acesso geral"><Button onClick={handleOpenGlobalHistory} sx={{ py: 1.5, px: 2 }}><History /></Button></Tooltip>
                                        <Tooltip title="Selecionar múltiplos"><Button onClick={handleToggleSelectionMode} sx={{ py: 1.5, px: 2 }}><Checklist /></Button></Tooltip>
                                        <Tooltip title="Exportar CSV"><Button onClick={handleExportCsv} disabled={isExporting} sx={{ py: 1.5, px: 2 }}>{isExporting ? <CircularProgress size={20} /> : <Download />}</Button></Tooltip>
                                        <Tooltip title="Importar CSV"><Button onClick={() => setIsImportModalOpen(true)} sx={{ py: 1.5, px: 2 }}><UploadFile /></Button></Tooltip>
                                    </ButtonGroup>
                                    <Button variant="contained" startIcon={<Add />} onClick={() => setModal({ type: 'create', data: null })} sx={{ py: '13px', px: 3, borderRadius: 2, width: { xs: '100%', sm: 'auto' } }} disabled={licenseUsage.used >= licenseUsage.limit}>Adicionar Membro</Button>
                                </Stack>
                            )}
                        </Stack>
                    </Stack>

                    <DarkPaper sx={{ p: 2, mb: 4, background: alpha('#90caf9', 0.05) }}>
                        <Stack spacing={1}>
                            <Stack direction="row" justifyContent="space-between">
                                <Typography variant="body2" fontWeight="bold" color="text.secondary">Uso da Licença</Typography>
                                <Typography variant="body2" fontWeight="bold">{licenseUsage.used} / {licenseUsage.limit} usuários</Typography>
                            </Stack>
                            <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 5, bgcolor: 'rgba(255,255,255,0.05)' }} />
                        </Stack>
                    </DarkPaper>

                    <DarkPaper sx={{ p: 2, mb: 4 }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <TextField
                                fullWidth variant="outlined"
                                placeholder="Pesquisar por nome, email ou CPF..."
                                value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{ startAdornment: (<InputAdornment position="start"><Search /></InputAdornment>) }}
                            />
                            <Button variant="outlined" startIcon={<FilterList />} onClick={handleOpenFilterModal} sx={{ py: 1.5, px: 4, whiteSpace: 'nowrap', borderColor: 'rgba(255,255,255,0.1)' }}>Filtrar</Button>
                        </Stack>
                    </DarkPaper>

                    {selectionModeActive && numSelected > 0 && (
                        <DarkPaper sx={{ mb: 2, background: alpha('#90caf9', 0.1) }}>
                            <Toolbar sx={{ pl: { sm: 2 }, pr: { xs: 1, sm: 1 } }}>
                                <Typography sx={{ flex: '1 1 100%' }} color="inherit" variant="subtitle1">{numSelected} selecionado(s)</Typography>
                                <Tooltip title="Vincular Softwares"><IconButton onClick={handleOpenSelectionSoftware}><Apps /></IconButton></Tooltip>
                                <Tooltip title="Desativar"><IconButton onClick={() => handleBulkAction('deactivate')}><GroupOff /></IconButton></Tooltip>
                                <Tooltip title="Excluir"><IconButton onClick={() => handleBulkAction('delete')}><DeleteForever /></IconButton></Tooltip>
                            </Toolbar>
                        </DarkPaper>
                    )}

                    {isMobile ? (
                        <Stack spacing={2}>
                            {loading ? (
                                <Box textAlign="center" sx={{ py: 10 }}><CircularProgress /></Box>
                            ) : error ? (
                                <Alert severity="error">{error}</Alert>
                            ) : (
                                <AnimatePresence>
                                    {filteredCollaborators.map((c) => (
                                        <CollaboratorCardMobile
                                            key={c.id}
                                            collaborator={c}
                                            isSelected={isSelected(c.id)}
                                            isSelf={c.id === user.id}
                                            selectionModeActive={selectionModeActive}
                                            onSelect={(e) => handleClick(e, c.id)}
                                            onEdit={() => setModal({ type: 'edit', data: c })}
                                            onDelete={() => setModal({ type: 'delete', data: c })}
                                            onHistory={() => handleOpenUserHistory(c)}
                                            onPermissions={() => handleOpenPermissions(c)}
                                            onToggleStatus={() => openConfirmToggle(c)}
                                            statusUpdatingId={statusUpdatingId}
                                            accessLevelConfig={accessLevelConfig}
                                        />
                                    ))}
                                </AnimatePresence>
                            )}
                        </Stack>
                    ) : (
                        <DarkPaper sx={{ borderRadius: 4, overflow: 'hidden' }}>
                            <TableContainer>
                                <Table>
                                    <TableHead sx={{ bgcolor: alpha('#90caf9', 0.04) }}>
                                        <TableRow>
                                            {selectionModeActive && (<TableCell padding="checkbox"><Checkbox color="primary" indeterminate={numSelected > 0 && numSelected < rowCount} checked={rowCount > 0 && numSelected === rowCount} onChange={handleSelectAllClick} /></TableCell>)}
                                            <TableCell sx={{ fontWeight: 'bold' }}>Nome Completo</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Cargo</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>CPF</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Unidade</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Último Acesso</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Acesso</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Ações</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {loading ? (
                                            <TableRow><TableCell colSpan={selectionModeActive ? 9 : 8} align="center" sx={{ py: 10 }}><CircularProgress /></TableCell></TableRow>
                                        ) : error ? (
                                            <TableRow><TableCell colSpan={selectionModeActive ? 9 : 8} align="center" sx={{ py: 10 }}><Alert severity="error">{error}</Alert></TableCell></TableRow>
                                        ) : (
                                            <AnimatePresence>
                                                {filteredCollaborators.map((collaborator) => {
                                                    const isItemSelected = isSelected(collaborator.id);
                                                    const isActive = collaborator.isActive !== false;
                                                    const avatarCompletoUrl = collaborator.avatarUrl ? new URL(`/uploads/${collaborator.avatarUrl}`, API_BASE_URL).href : '';
                                                    const isSelf = collaborator.id === user.id;
                                                    return (
                                                        <motion.tr
                                                            key={collaborator.id} layout
                                                            style={{
                                                                backgroundColor: isItemSelected ? alpha(customTheme.palette.primary.main, 0.1) : 'transparent',
                                                                cursor: selectionModeActive && !isSelf ? 'pointer' : 'default'
                                                            }}
                                                            onClick={(event) => selectionModeActive && handleClick(event, collaborator.id)}
                                                        >
                                                            {selectionModeActive && (<TableCell padding="checkbox"><Checkbox color="primary" checked={isItemSelected} disabled={isSelf} /></TableCell>)}
                                                            <TableCell>
                                                                <Stack direction="row" alignItems="center" spacing={2}>
                                                                    <Avatar sx={{ bgcolor: 'primary.main', color: 'primary.contrastText' }} src={avatarCompletoUrl}>
                                                                        {!collaborator.avatarUrl && collaborator.name ? collaborator.name.split(' ').map(n => n[0]).join('').toUpperCase() : ''}
                                                                    </Avatar>
                                                                    <Typography variant="body2" fontWeight="500">{collaborator.name}</Typography>
                                                                </Stack>
                                                            </TableCell>
                                                            <TableCell><Typography variant="body2">{collaborator.cargo || 'Não definido'}</Typography></TableCell>
                                                            <TableCell><Typography variant="body2">{collaborator.cpf || '---'}</Typography></TableCell>
                                                            <TableCell>
                                                                {collaborator.unit ? (
                                                                    <Chip label={`${collaborator.unit.name} ${collaborator.unit.isMatriz ? '(Matriz)' : ''}`} size="small" variant="outlined" color={collaborator.unit.isMatriz ? 'primary' : 'default'} />
                                                                ) : (<Chip label="N/A" size="small" variant="outlined" />)}
                                                            </TableCell>
                                                            <TableCell><Typography variant="body2">{collaborator.lastLogin ? format(parseISO(collaborator.lastLogin), 'dd/MM/yyyy HH:mm', { locale: ptBR }) : 'Nunca acessou'}</Typography></TableCell>
                                                            <TableCell><Chip label={accessLevelConfig[collaborator.role]?.label || collaborator.role} color={accessLevelConfig[collaborator.role]?.color || 'default'} size="small" /></TableCell>
                                                            <TableCell><Chip icon={<CheckCircle fontSize="small" />} label={isActive ? 'Ativo' : 'Desligado'} color={isActive ? 'success' : 'error'} size="small" /></TableCell>
                                                            <TableCell align="right">
                                                                <Tooltip title="Editar"><IconButton size="small" onClick={(e) => { e.stopPropagation(); setModal({ type: 'edit', data: collaborator }); }}><ModeEditOutline fontSize="small" /></IconButton></Tooltip>
                                                                {!isSelf && (<Tooltip title="Permissões"><IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenPermissions(collaborator); }}><Security fontSize="small" /></IconButton></Tooltip>)}
                                                                <Tooltip title="Histórico"><IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenUserHistory(collaborator); }}><History fontSize="small" /></IconButton></Tooltip>
                                                                <Tooltip title={isActive ? 'Desativar' : 'Reativar'}>
                                                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); !isSelf && openConfirmToggle(collaborator); }} disabled={isSelf || statusUpdatingId === collaborator.id}>
                                                                        {statusUpdatingId === collaborator.id ? <CircularProgress size={20} /> : <PowerSettingsNew fontSize="small" color={isActive ? 'error' : 'success'} />}
                                                                    </IconButton>
                                                                </Tooltip>
                                                                <Tooltip title="Remover"><IconButton size="small" onClick={(e) => { e.stopPropagation(); !isSelf && setModal({ type: 'delete', data: collaborator }); }} disabled={isSelf}><DeleteOutline fontSize="small" /></IconButton></Tooltip>
                                                            </TableCell>
                                                        </motion.tr>
                                                    );
                                                })}
                                            </AnimatePresence>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </DarkPaper>
                    )}
                </Container>

                {/* MODAIS */}
                <Dialog open={openSelectionSoftwareModal} onClose={() => setOpenSelectionSoftwareModal(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
                    <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                            <Typography variant="h6">Atribuir Softwares em Massa</Typography>
                            <Typography variant="body2" color="text.secondary">Selecionado(s): {selected.length} colaborador(es)</Typography>
                        </Box>
                        <IconButton onClick={() => setOpenSelectionSoftwareModal(false)}><Close /></IconButton>
                    </DialogTitle>
                    <DialogContent dividers sx={{ borderColor: 'transparent' }}>
                        <Alert severity="info" sx={{ mb: 2 }}>Os softwares serão <strong>adicionados</strong>. Permissões existentes serão mantidas.</Alert>
                        {availableSoftwares.length === 0 ? (
                            <Box sx={{ textAlign: 'center', py: 3 }}><Typography color="text.secondary">Nenhum software disponível.</Typography></Box>
                        ) : (
                            <Stack spacing={1}>
                                {availableSoftwares.map(software => (
                                    <Paper key={software.id} variant="outlined" sx={{
                                        p: 1,
                                        borderColor: selectionSoftwares.includes(software.id) ? 'primary.main' : 'rgba(255,255,255,0.05)',
                                        backgroundColor: selectionSoftwares.includes(software.id) ? alpha('#90caf9', 0.05) : 'transparent',
                                        borderRadius: 2
                                    }}>
                                        <FormControlLabel
                                            control={<Checkbox checked={selectionSoftwares.includes(software.id)} onChange={() => handleToggleSelectionSoftware(software.id)} />}
                                            label={<Typography variant="body2" fontWeight="bold">{software.name}</Typography>}
                                            sx={{ width: '100%', m: 0 }}
                                        />
                                    </Paper>
                                ))}
                            </Stack>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ p: 2 }}>
                        <Button onClick={() => setOpenSelectionSoftwareModal(false)}>Cancelar</Button>
                        <Button onClick={handleSaveSelectionSoftware} variant="contained" disabled={savingSelectionSoftwares || selectionSoftwares.length === 0} startIcon={savingSelectionSoftwares ? <CircularProgress size={20} color="inherit" /> : <Save />}>
                            {savingSelectionSoftwares ? 'Salvando...' : 'Salvar para Todos'}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={openPermissionsModal} onClose={() => setOpenPermissionsModal(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
                    <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                            <Typography variant="h6">Permissões de Software</Typography>
                            <Typography variant="body2" color="text.secondary">Colaborador: {userForPermissions?.name}</Typography>
                        </Box>
                        <IconButton onClick={() => setOpenPermissionsModal(false)}><Close /></IconButton>
                    </DialogTitle>
                    <DialogContent dividers sx={{ borderColor: 'transparent' }}>
                        {availableSoftwares.length === 0 ? (
                            <Box sx={{ textAlign: 'center', py: 3 }}><Typography color="text.secondary">Sua empresa ainda não possui softwares habilitados.</Typography></Box>
                        ) : (
                            <Stack spacing={1}>
                                <Typography variant="subtitle2" gutterBottom sx={{ color: 'secondary.main', fontWeight: 'bold' }}>Selecione os sistemas que este usuário pode acessar:</Typography>
                                {availableSoftwares.map(software => (
                                    <Paper key={software.id} variant="outlined" sx={{
                                        p: 1,
                                        borderColor: userSoftwares.includes(software.id) ? 'primary.main' : 'rgba(255,255,255,0.05)',
                                        backgroundColor: userSoftwares.includes(software.id) ? alpha('#90caf9', 0.05) : 'transparent',
                                        borderRadius: 2
                                    }}>
                                        <FormControlLabel
                                            control={<Checkbox checked={userSoftwares.includes(software.id)} onChange={() => handleTogglePermission(software.id)} />}
                                            label={<Typography variant="body2" fontWeight="bold">{software.name}</Typography>}
                                            sx={{ width: '100%', m: 0 }}
                                        />
                                    </Paper>
                                ))}
                            </Stack>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ p: 2 }}>
                        <Button onClick={() => setOpenPermissionsModal(false)}>Cancelar</Button>
                        <Button onClick={handleSavePermissions} variant="contained" disabled={savingPermissions || availableSoftwares.length === 0} startIcon={savingPermissions ? <CircularProgress size={20} color="inherit" /> : <Save />}>
                            {savingPermissions ? 'Salvando...' : 'Salvar Permissões'}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={isFilterModalOpen} onClose={handleCloseFilterModal} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 3 } }}>
                    <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        Opções de Filtro <IconButton onClick={handleCloseFilterModal}><Close /></IconButton>
                    </DialogTitle>
                    <DialogContent dividers sx={{ borderColor: 'transparent' }}>
                        <Stack spacing={3}>
                            <Box>
                                <Typography variant="subtitle1" fontWeight={500} sx={{ mb: 1 }}>Filtrar por Status</Typography>
                                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                                    <Chip label="Todos" onClick={() => setTempFilterStatus('all')} color="primary" variant={tempFilterStatus === 'all' ? 'filled' : 'outlined'} />
                                    <Chip label="Ativos" onClick={() => setTempFilterStatus('active')} color="success" variant={tempFilterStatus === 'active' ? 'filled' : 'outlined'} />
                                    <Chip label="Desligados" onClick={() => setTempFilterStatus('inactive')} color="error" variant={tempFilterStatus === 'inactive' ? 'filled' : 'outlined'} />
                                </Stack>
                            </Box>
                            <Box>
                                <Typography variant="subtitle1" fontWeight={500} sx={{ mb: 1 }}>Filtrar por Nível de Acesso</Typography>
                                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                                    <Chip label="Todos" onClick={() => setTempFilterRole('all')} color="primary" variant={tempFilterRole === 'all' ? 'filled' : 'outlined'} />
                                    {accessLevels.map(level => (<Chip key={level.value} label={level.label} onClick={() => setTempFilterRole(level.value)} color={level.color} variant={tempFilterRole === level.value ? 'filled' : 'outlined'} />))}
                                </Stack>
                            </Box>
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={{ p: 2 }}>
                        <Button onClick={handleClearFilters}>Limpar Filtros</Button>
                        <Button onClick={handleApplyFilters} variant="contained">Aplicar</Button>
                    </DialogActions>
                </Dialog>

                <LoginHistoryModal open={historyModalOpen} onClose={() => setHistoryModalOpen(false)} logs={historyLogs} title={historyTitle} loading={historyLoading} />
                <BulkImportModal open={isImportModalOpen} onClose={() => setIsImportModalOpen(false)} onImport={handleImport} units={units} />
                <UserFormModal open={modal.type === 'create' || modal.type === 'edit'} onClose={handleCloseModal} onSubmit={handleSaveUser} initialData={modal.data} units={units} />

                <Dialog open={modal.type === 'delete'} onClose={handleCloseModal} PaperProps={{ sx: { borderRadius: 3 } }}>
                    <DialogTitle fontWeight="bold">Confirmar Remoção</DialogTitle>
                    <DialogContent>
                        <Typography>Você tem certeza que deseja remover o colaborador <Typography component="span" fontWeight="bold">{modal.data?.name}</Typography>?</Typography>
                    </DialogContent>
                    <DialogActions sx={{ p: '16px 24px' }}>
                        <Button onClick={handleCloseModal}>Cancelar</Button>
                        <Button onClick={handleDeleteUser} variant="contained" color="error">Confirmar Remoção</Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={confirmDialog.type === 'toggleStatus'} onClose={closeConfirmToggle} PaperProps={{ sx: { borderRadius: 3 } }}>
                    <DialogTitle>Confirmar Alteração de Status</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Tem certeza que deseja {confirmDialog.user?.isActive !== false ? 'desativar' : 'reativar'} o colaborador <strong>{confirmDialog.user?.name}</strong>?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={closeConfirmToggle}>Cancelar</Button>
                        <Button onClick={handleToggleStatus} variant="contained" color={confirmDialog.user?.isActive !== false ? 'error' : 'success'}>Confirmar</Button>
                    </DialogActions>
                </Dialog>

                <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                    <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
                </Snackbar>
            </Box>
        </ThemeProvider>
    );
};

export default CollaboratorsPage;